// eslint-disable-next-line import/order
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Grid, IconButton, styled } from '@mui/material';
import Box from '@mui/material/Box';
import { generateUnwoundRowIdFromMetadata } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { find, findIndex, first, map, size } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { useCasesAndSlidesEditingPermissions } from '../../useCasesAndSlidesEditingPermissions';
import { useStudyPlatformSettings } from 'utils/useStudyPlatformSettings';
import { CardMode } from '..';
import CardCheckbox from './CardCheckbox';
import ItemsCountBadge from './ItemsCountBadge';
import SlideCarouselItem from './SlideCarouselItem';
import { getSlideCarouselTone, getSlideNavigationSurfaceStyles, SlideCarouselTone } from './slideCarouselChrome';
import { defaultSlideCarouselPresentation, SlideCarouselPresentation } from './slideCarouselPresentation';

export const StyledCarousel = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
}));

const ArrowButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: SlideCarouselTone }>(({ theme, tone }) => {
  const navigationSurfaceStyles = getSlideNavigationSurfaceStyles(theme, tone);

  return {
    position: 'absolute',
    borderRadius: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: '32px',
    height: '32px',
    minWidth: 0,
    padding: 0,
    color: navigationSurfaceStyles.color,
    ...navigationSurfaceStyles,
    backgroundColor: `${navigationSurfaceStyles.backgroundColor} !important`,
    backgroundImage: navigationSurfaceStyles.backgroundImage,
    border: navigationSurfaceStyles.border,
    boxShadow: navigationSurfaceStyles.boxShadow,
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
    '&:hover': {
      ...navigationSurfaceStyles,
      backgroundColor: `${navigationSurfaceStyles.backgroundColor} !important`,
      backgroundImage: navigationSurfaceStyles.backgroundImage,
      border: navigationSurfaceStyles.border,
    },
    '&.back': {
      left: '4px',
    },
    '&.next': {
      right: '4px',
    },
  };
});

interface Props {
  slides: Slide[];
  onChangeSlide?: (s: Slide) => void;
  presentation?: SlideCarouselPresentation;
  mode?: CardMode;
  caseData?: Procedure;
  forceDisplayCheckbox?: boolean;
  selectable?: boolean;
}

const getStainIndex = (slides: Slide[], stainingType: string): number | undefined => {
  if (!stainingType) return undefined;
  const currentStainIndex = findIndex(slides, (slide) => slide.stainingType === stainingType);
  return currentStainIndex === -1 ? undefined : currentStainIndex;
};

const SlideCarouselControls: React.FC<
  React.PropsWithChildren<{
    slides: Slide[];
    currentIndex: number;
    setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
    currentSlide?: Slide;
    mode?: CardMode;
  }>
> = ({ slides, currentIndex, setCurrentIndex, currentSlide, mode }) => {
  const slideCount = size(slides);
  const lastSlideIndex = slideCount - 1;
  const tone = getSlideCarouselTone(currentSlide?.stainingType);

  const handleNextClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (currentIndex >= lastSlideIndex) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (currentIndex === 0) {
      setCurrentIndex(lastSlideIndex);
    } else {
      setCurrentIndex(currentIndex - 1);
    }
  };

  return (
    <>
      <ArrowButton onClick={handlePrevClick} className="back" tone={tone} aria-label="previous slide">
        <ArrowBackIosIcon sx={{ marginLeft: '3px' }} />
      </ArrowButton>

      <ArrowButton onClick={handleNextClick} className="next" tone={tone} aria-label="next slide">
        <ArrowForwardIosIcon />
      </ArrowButton>

      <ItemsCountBadge
        slideCount={slideCount}
        caseHasQcFailedSlide={Boolean(find(slides, (slide) => slide.qcFailed))}
        showWarning={mode === 'full'}
      />
    </>
  );
};

const SlideCarouselViewport: React.FC<
  React.PropsWithChildren<{
    rowId?: string | number;
    slides: Slide[];
    currentIndex: number;
    presentation: SlideCarouselPresentation;
    mode: CardMode;
    setHovered: React.Dispatch<React.SetStateAction<boolean>>;
    trackRef: React.RefObject<HTMLDivElement>;
    shouldShowCheckbox: boolean;
    hasMetadataEditingPermissions: boolean;
  }>
> = ({
  children,
  rowId,
  slides,
  currentIndex,
  presentation,
  mode,
  setHovered,
  trackRef,
  shouldShowCheckbox,
  hasMetadataEditingPermissions,
}) => {
  return (
    <Box
      data-cy={`slide-carousel-rowId-${rowId}`}
      onMouseOver={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      <Grid container ref={trackRef} flexWrap="nowrap">
        {map(slides, (slide, index) => (
          <SlideCarouselItem
            key={slide.id}
            slide={slide}
            active={currentIndex === index}
            presentation={presentation}
            mode={mode}
          >
            {children}
          </SlideCarouselItem>
        ))}
      </Grid>
      {shouldShowCheckbox && hasMetadataEditingPermissions && (
        <Grid item container sx={{ position: 'absolute', top: 0, left: 0 }}>
          <CardCheckbox id={rowId} />
        </Grid>
      )}
    </Box>
  );
};

const SlideCarousel: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  children,
  slides,
  onChangeSlide,
  presentation = defaultSlideCarouselPresentation,
  mode = 'full',
  caseData,
  forceDisplayCheckbox,
  selectable,
}) => {
  const { queryParams } = useEncodedFilters();
  const filteredStainingType: string | undefined = first(queryParams?.filters?.stains);

  const { defaultStainingType: defaultStainingTypeFromStudy } = useStudyPlatformSettings(caseData?.studyId);

  const stainingTypeToDisplay = filteredStainingType || defaultStainingTypeFromStudy;

  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);

  const slidesCount = size(slides);
  if (slidesMode && slidesCount > 1) {
    console.warn('slides mode is on but there are more than one slide');
  }

  const { hasMetadataEditingPermissions } = useCasesAndSlidesEditingPermissions();

  const [hovered, setHovered] = React.useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const [slidingCurrentIndex, setSlidingCurrentIndex] = useState(
    stainingTypeToDisplay ? getStainIndex(slides, stainingTypeToDisplay) ?? 0 : 0
  );
  const currentIndex = slidesCount > 0 ? slidingCurrentIndex % slidesCount : 0;
  const currentSlide = slides[currentIndex];

  const rowId = slidesMode
    ? generateUnwoundRowIdFromMetadata({
        baseRowId: caseData?.id,
        innerRowId: first(slides)?.id,
      })
    : caseData?.id;

  // Reset the current index when the stain type filter / default stain type changes
  useEffect(() => {
    setSlidingCurrentIndex((previous) =>
      stainingTypeToDisplay ? getStainIndex(slides, stainingTypeToDisplay) ?? previous : previous
    );
  }, [slides, stainingTypeToDisplay]);

  // Animate the carousel when the current index changes
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    ref.current.style.transition = 'all 0.2s ease-in-out';
    ref.current.style.transform = `translateX(-${currentIndex}00%)`;

    if (onChangeSlide && slides[currentIndex]) {
      onChangeSlide(slides[currentIndex]);
    }
  }, [slides, currentIndex, onChangeSlide]);

  const shouldShowCheckbox = selectable && (forceDisplayCheckbox || hovered);

  return (
    <StyledCarousel>
      <SlideCarouselViewport
        rowId={rowId}
        slides={slides}
        currentIndex={currentIndex}
        presentation={presentation}
        mode={mode}
        setHovered={setHovered}
        trackRef={ref}
        shouldShowCheckbox={shouldShowCheckbox}
        hasMetadataEditingPermissions={hasMetadataEditingPermissions}
      >
        {children}
      </SlideCarouselViewport>
      {/* Don't display controls if there is only one slide or if we are displaying a static slide */}
      {slidesCount > 1 && onChangeSlide && (
        <SlideCarouselControls
          currentIndex={currentIndex}
          setCurrentIndex={setSlidingCurrentIndex}
          currentSlide={currentSlide}
          slides={slides}
          mode={mode}
        />
      )}
    </StyledCarousel>
  );
};

export default SlideCarousel;
